import React, { useEffect, useState } from "react";
import * as signalR from "@microsoft/signalr";
import {
  getAccessToken,
  isSessionRenewalFailing,
  refreshSession,
  subscribeSessionRenewal,
} from "../utils/config/apiClient";
import { SignalRUrl } from "../utils/config/config";
import { GetToken } from "../utils/helper/helper";
import { useSelector } from "react-redux";

/**
 * How long to wait before each attempt to (re)connect: SignalR's automatic reconnect walks this
 * list and then keeps the last delay, and a failed start or a close does the same.
 */
export const RETRY_DELAYS_MS = [0, 2000, 5000, 10000, 30000];

/** The delay for the attempt after `previousAttempts` failed ones. */
export const retryDelay = (previousAttempts) =>
  RETRY_DELAYS_MS[Math.min(previousAttempts, RETRY_DELAYS_MS.length - 1)];

/**
 * The live feed's state, published as `connectionStatus` for the banner (LiveFeedBanner):
 *   connecting    - first start, not up yet
 *   connected     - up
 *   reconnecting  - was up, dropped, being brought back - or a first start that already failed
 *                   and is being retried (#182: LiveFeedBanner must stay up through the retries)
 *   disconnected  - not up, and a retry is scheduled (or signed out)
 */
/**
 * Whether a failed start or reconnect was the server refusing the token (#182). The negotiate
 * request's 401 comes back as an HttpError with statusCode 401, or - once SignalR has wrapped it in
 * FailedToNegotiateWithServerError - only in the message ("... Status code '401'").
 *
 * @param {unknown} error What start() rejected with, or a reconnect's retryReason.
 * @returns {boolean}
 */
export const isUnauthorized = (error) =>
  error?.statusCode === 401 || /Status code '401'/.test(String(error?.message ?? error ?? ""));

export const CONNECTION_STATUS = {
  connecting: "connecting",
  connected: "connected",
  reconnecting: "reconnecting",
  disconnected: "disconnected",
};

export const Context = React.createContext();
export const ContextProvider = ({ children }) => {
  const [hubConnection, setHubConnection] = useState(0);
  // Bumped each time the connection comes back after a drop - by automatic reconnect or by a
  // restart after it gave up. Messages sent while it was down are lost, so a screen may need to
  // refetch (#118, useHubResync).
  const [reconnectCount, setReconnectCount] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATUS.disconnected);
  // #182: the session's renewal is failing without a refusal (apiClient), for LiveFeedBanner.
  const [sessionRenewalFailing, setSessionRenewalFailing] = useState(isSessionRenewalFailing);
  const { authtoken } = useSelector((state) => state.auth);

  useEffect(() => {
    setSessionRenewalFailing(isSessionRenewalFailing());
    return subscribeSessionRenewal(setSessionRenewalFailing);
  }, []);

  // #182: keyed on WHETHER there is a session, not on the token string. TopNavbar publishes the
  // stored token on every mount, so after each renewal the first page change used to tear a healthy
  // connection down and build a new one, losing whatever was sent in between. The token itself
  // always comes from getAccessToken(), which SignalR asks on every connect.
  const signedIn = Boolean(authtoken);

  useEffect(() => {
    if (!signedIn) {
      setHubConnection(0);
      setConnectionStatus(CONNECTION_STATUS.disconnected);
      return undefined;
    }

    // #182: ONE connection per sign-in, with ONE recovery routine.
    //   * No skipNegotiation / WebSockets-only transport any more: the client negotiates and falls
    //     back to long polling where WebSockets are blocked (corporate, hotel and some mobile
    //     networks), which used to mean no live updates at all. Server-Sent Events are left out
    //     (#182 QA, rows 1832/1373): behind a buffering proxy the event stream "opens" but never
    //     delivers, the handshake then times out, and SignalR retries SSE forever instead of moving
    //     on to long polling.
    //   * Automatic reconnect never gives up while there is a token (it used to stop after four
    //     tries), and a start that fails - which automatic reconnect does not cover - or a close is
    //     retried by start() on the SAME object. The connection used to be thrown away and a new one
    //     built 5 s later, so handlers had to be moved to it and the screens could not tell a
    //     reconnect from a first connection.
    //   * The state is published, so a dead feed shows instead of looking like a quiet one.
    // #104 still holds: getAccessToken() is asked on every connect and renews a token about to
    // expire; a renewal the server refuses ends the session, which empties GetToken() and stops the
    // retries below.
    let disposed = false;
    let retryTimer = null;
    let failedStarts = 0;
    let hasConnected = false;
    // #182: a 401 on start or reconnect - the token was revoked (a sign-in elsewhere ends this
    // session) or has run out. Retrying with it can only get 401 again, and used to do so every
    // 30 s behind a "reconnecting" banner. One renewal at a time, and at most one that WORKED per
    // outage: a renewed token that is still refused is not renewed again until the feed is back.
    let renewing = false;
    let renewedThisOutage = false;

    // Ends the automatic reconnect or failed start that got the 401 (the caller stops retrying):
    // the session is renewed first, and start() runs again once that has settled. A refused
    // renewal has ended the session - forceLogout, back to the sign-in screen with a notice - and
    // with no token scheduleStart does nothing; one that got no reply keeps the retries going.
    // Returns false when there is nothing to renew for, and the caller just retries.
    const renewAfterUnauthorized = () => {
      if (renewedThisOutage || !GetToken()) return false;
      if (renewing) return true;
      renewing = true;
      refreshSession(GetToken()).then((token) => {
        renewing = false;
        if (token) renewedThisOutage = true;
        scheduleStart();
      });
      return true;
    };

    const hubConnect = new signalR.HubConnectionBuilder()
      .withUrl(SignalRUrl, {
        accessTokenFactory: getAccessToken,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: ({ previousRetryCount, retryReason }) => {
          if (!GetToken()) return null;
          // null closes the connection; onclose leaves the restart to the renewal.
          if (isUnauthorized(retryReason) && renewAfterUnauthorized()) return null;
          return retryDelay(previousRetryCount);
        },
      })
      // #197: without a logger the HubConnection gets a NullLogger, so a handler that threw
      // ("A callback for the method ... threw error") vanished without a trace. Error, not
      // Warning: Warning also reports every push the current screen has no handler for, which
      // the resale status does on every Worker cycle.
      .configureLogging(signalR.LogLevel.Error)
      .build();

    const scheduleStart = () => {
      // While a renewal is out, its end schedules the start - with the token it brought.
      if (disposed || renewing || retryTimer !== null || !GetToken()) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        start();
      }, retryDelay(failedStarts));
    };

    const start = async () => {
      if (disposed) return;
      // #182: a retry after a failed start is "reconnecting", not "connecting" - the banner counts
      // only reconnecting/disconnected as down, so it used to vanish for every (15 s) attempt.
      setConnectionStatus(
        hasConnected || failedStarts > 0 ? CONNECTION_STATUS.reconnecting : CONNECTION_STATUS.connecting
      );
      try {
        await hubConnect.start();
      } catch (error) {
        failedStarts += 1;
        setConnectionStatus(CONNECTION_STATUS.disconnected);
        if (!(isUnauthorized(error) && renewAfterUnauthorized())) scheduleStart();
        return;
      }

      if (disposed) {
        hubConnect.stop();
        return;
      }

      failedStarts = 0;
      renewedThisOutage = false;
      setConnectionStatus(CONNECTION_STATUS.connected);
      // A restart after the connection was lost is a reconnect for the screens too.
      if (hasConnected) setReconnectCount((count) => count + 1);
      hasConnected = true;
    };

    hubConnect.onreconnecting(() => {
      setConnectionStatus(CONNECTION_STATUS.reconnecting);
    });

    hubConnect.onreconnected(() => {
      renewedThisOutage = false;
      setConnectionStatus(CONNECTION_STATUS.connected);
      setReconnectCount((count) => count + 1);
    });

    // Fires after stop(), or once automatic reconnect has given up - which it only does when the
    // token is gone. Start the same connection again while there is still a session.
    hubConnect.onclose(() => {
      if (disposed) return;
      setConnectionStatus(CONNECTION_STATUS.disconnected);
      scheduleStart();
    });

    // Published before it starts: handlers registered with on() (useHubEvent) survive stop and
    // start of the same object, so they are attached before the first message can arrive.
    setHubConnection(hubConnect);
    start();

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      hubConnect.stop();
      setHubConnection(0);
      setConnectionStatus(CONNECTION_STATUS.disconnected);
    };
  }, [signedIn]);

  return (
    <Context.Provider
      value={{ hubConnection, setHubConnection, reconnectCount, connectionStatus, sessionRenewalFailing }}
    >
      {children}
    </Context.Provider>
  );
};
